import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Languages, Sparkles } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const { t, lang, toggleLang } = useI18n();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/chat");
        } catch (e) {
            const status = e?.response?.status;
            if (!e.response || status >= 500 || status === 502 || status === 504) {
                setErr(t.auth.serverWaking);
            } else {
                setErr(e?.response?.data?.detail || t.auth.loginError);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left visual */}
            <div className="hidden lg:flex flex-1 relative bg-background-alt overflow-hidden">
                <div className="absolute inset-0" style={{
                    backgroundImage: "url('https://images.pexels.com/photos/7794004/pexels-photo-7794004.jpeg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.15,
                }} />
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">F</div>
                        <div className="font-display text-xl font-semibold">FiscoAI</div>
                    </div>
                    <div className="space-y-6 max-w-md">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase">
                            <Sparkles size={12} /> AI per commercialisti
                        </div>
                        <h1 className="font-display text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
                            La compilazione fiscale, <em className="text-primary not-italic">conversazionale</em>.
                        </h1>
                        <p className="text-base text-muted-foreground leading-relaxed">
                            FiscoAI compila modelli AA9/12 e altri adempimenti ascoltandoti come un assistente reale. Tu parli, lui prepara — tu confermi, lui genera.
                        </p>
                        <div className="grid grid-cols-3 gap-4 pt-4">
                            <Stat n="< 60s" label="Compilazione AA9/12" />
                            <Stat n="100%" label="File conforme AdE" />
                            <Stat n="IT · AR" label="Bilingue + RTL" />
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground">© FiscoAI · Software professionale</div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
                <div className="w-full max-w-md">
                    <div className="flex items-center justify-between mb-10">
                        <Link to="/login" className="lg:hidden flex items-center gap-2">
                            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">F</div>
                            <span className="font-display text-lg font-semibold">FiscoAI</span>
                        </Link>
                        <button
                            data-testid="auth-language-switcher"
                            onClick={toggleLang}
                            className="ms-auto flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Languages size={14} /> {lang === "it" ? "IT → AR" : "AR → IT"}
                        </button>
                    </div>

                    <h2 className="font-display text-3xl font-medium tracking-tight mb-2">{t.auth.loginTitle}</h2>
                    <p className="text-sm text-muted-foreground mb-8">{t.auth.loginSubtitle}</p>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        <Field label={t.auth.email}>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm transition"
                                placeholder="nome@esempio.it"
                            />
                        </Field>
                        <Field label={t.auth.password}>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm transition"
                                placeholder="••••••••"
                            />
                        </Field>
                        {err && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{err}</div>}
                        <button
                            type="submit"
                            data-testid="login-submit"
                            disabled={loading}
                            className="w-full px-6 py-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm transition-colors disabled:opacity-50"
                        >
                            {loading ? "..." : t.auth.loginBtn}
                        </button>
                    </form>
                    <div className="mt-6 text-sm text-muted-foreground text-center">
                        {t.auth.noAccount}{" "}
                        <Link data-testid="login-to-register" to="/register" className="text-primary font-medium hover:underline">{t.auth.registerLink}</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}

function Stat({ n, label }) {
    return (
        <div>
            <div className="font-display text-xl font-medium">{n}</div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}
